import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'

import { useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext } from '../session'
import type { Page } from '../useCollection'

interface AuditLog {
  id: string
  created_at: string
  actor_label: string
  action: string
  entity_type: string
  entity_id: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  reason: string | null
}

/**
 * Le journal : qui a fait quoi, et quand.
 *
 * Le journal existait depuis le premier jour — chaque service y écrit — mais
 * aucun écran ne le montrait. Une trace que personne ne peut lire ne protège
 * personne : elle ne sert qu'à croire qu'on est couvert.
 *
 * **Rien ne s'écrit ni ne s'efface ici**, et c'est le point. Une ligne
 * modifiable ne prouverait plus rien ; le journal est en lecture seule côté
 * serveur, et cet écran n'a donc aucun bouton d'action.
 *
 * Le filtre par action est un champ libre plutôt qu'une liste : les actions se
 * nomment `module.ressource.geste`, et taper « finance » suffit à voir tout
 * l'argent. Une liste déroulante aurait figé une nomenclature qui grandit à
 * chaque fonctionnalité.
 */
export function AdminAuditPage() {
  const { t } = useTranslation()
  const { eventId } = useAdminContext()
  const [action, setAction] = useState('')
  // On attend une courte pause dans la frappe : sans elle, « finance »
  // lançait sept requêtes, une par lettre.
  const [filter, setFilter] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setFilter(action.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [action])
  const [page, setPage] = useState(1)
  const [openRow, setOpenRow] = useState<string | null>(null)

  useEffect(() => {
    document.title = t('admin.section.audit')
  }, [t])

  const query = useAdminQuery<Page<AuditLog>>(
    ['admin-audit', eventId, filter, page],
    `/admin/audit-logs?page=${page}&page_size=50&event_id=${eventId}&include_platform=true` +
      (filter ? `&action=${encodeURIComponent(filter)}` : ''),
    eventId,
  )

  return (
    <>
      <AdminHeader
        title={t('admin.section.audit')}
        description={t('admin.audit.intro')}
      />

      <div className="mb-5 max-w-md">
        <label className="block text-caption uppercase tracking-wide text-content-muted">
          {t('admin.audit.filter')}
        </label>
        <input
          value={action}
          placeholder={t('admin.audit.filterPlaceholder')}
          onChange={(event) => {
            setAction(event.target.value)
            // Un filtre resserré sur la page sept n'affiche rien et laisse
            // croire qu'il n'y a pas de trace. On repart de la première.
            setPage(1)
          }}
          className="mt-1.5 w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
        />
      </div>

      {query.isPending ? <Skeleton className="h-96 w-full" /> : null}
      {query.isError ? (
        <p className="text-sm text-state-danger">{query.error.message}</p>
      ) : null}

      {query.data && query.data.items.length === 0 ? (
        <p className="text-sm text-content-secondary">{t('admin.audit.empty')}</p>
      ) : null}

      <div className="grid gap-1.5">
        {(query.data?.items ?? []).map((row) => (
          <AuditRow
            key={row.id}
            row={row}
            open={openRow === row.id}
            onToggle={() => setOpenRow(openRow === row.id ? null : row.id)}
          />
        ))}
      </div>

      {query.data && query.data.pages > 1 ? (
        <div className="mt-6 flex items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            {t('admin.audit.previous')}
          </Button>
          <span className="text-caption tabular-nums text-content-muted">
            {page} / {query.data.pages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= query.data.pages}
            onClick={() => setPage((current) => current + 1)}
          >
            {t('admin.audit.next')}
          </Button>
        </div>
      ) : null}
    </>
  )
}

function AuditRow({
  row,
  open,
  onToggle,
}: {
  row: AuditLog
  open: boolean
  onToggle: () => void
}) {
  const { t, i18n } = useTranslation()
  const detailed = row.before !== null || row.after !== null || row.reason !== null

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        disabled={!detailed}
        className="w-full px-4 py-2.5 text-left"
      >
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="shrink-0 text-caption tabular-nums text-content-muted">
            {new Date(row.created_at).toLocaleString(i18n.language, {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <span className="min-w-0 flex-1 truncate font-mono text-sm text-content-primary">
            {row.action}
          </span>
          <Badge tone="neutral">{row.actor_label}</Badge>
          {detailed ? (
            <span aria-hidden className="shrink-0 text-content-muted">
              {open ? '−' : '+'}
            </span>
          ) : null}
        </span>
      </button>

      {open ? (
        <div className="border-t border-line-subtle px-4 py-3">
          {row.reason ? (
            <p className="mb-2 text-sm text-content-secondary">{row.reason}</p>
          ) : null}
          {/* Avant et après, bruts. Les mettre en forme demanderait de
              connaître chaque type d'entité, et un affichage « propre » qui
              omet un champ vaut moins qu'un affichage brut qui les montre
              tous — c'est précisément le champ manquant qu'on vient chercher. */}
          <div className="grid gap-3 sm:grid-cols-2">
            {row.before ? (
              <Snapshot title={t('admin.audit.before')} value={row.before} />
            ) : null}
            {row.after ? (
              <Snapshot title={t('admin.audit.after')} value={row.after} />
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  )
}

function Snapshot({ title, value }: { title: string; value: Record<string, unknown> }) {
  return (
    <div className="min-w-0">
      <p className="text-caption uppercase tracking-wide text-content-muted">{title}</p>
      <pre className="mt-1 overflow-x-auto rounded-md bg-bg-overlay p-2 font-mono text-caption text-content-secondary">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}
